import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';
import { personName } from '../../desktop/taskText';
import BotAvatar from '../bots/BotAvatar';

const fold = (s) => String(s || '').toLocaleLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
const SHOWN = 8;

/** The people a task in this project can be given to: its team's members (none outside a team). */
export function peopleOf(project, teams) {
  if (!project?.team_id) return [];
  const team = (teams || []).find((tm) => tm.id === project.team_id);
  return (team?.members || []).filter((m) => m.user_id && personName(m));
}

/**
 * Suggestions while a quick task is typed, the same in every quick input (New, Home, the
 * table's add row, the desktop panel): "#" lists projects (with their team), "@" the people
 * of the task's team and the bots that may take it, "/" the project's lists, "+" labels. Up/Down move, Enter or Tab puts the whole name in ("#Lumen Co - Site "),
 * Escape closes; a tap picks on phones. The host passes its field's value and setter and
 * spreads `bind` on the field; `onKeyDown(e)` returns true when it used the key.
 */
export function useTokenSuggest({ value, setValue, inputRef, projects = [], teams = [], people = [], sections = [], labels = [] }) {
  const { t } = useTranslation();
  const [caret, setCaret] = useState(null);
  const [index, setIndex] = useState(0);
  const [closedAt, setClosedAt] = useState(null);

  // The "#..." or "@..." being typed: from the last sign at the start of a word up to the caret.
  const token = useMemo(() => {
    const end = caret ?? value.length;
    const before = value.slice(0, end);
    const m = before.match(/(?:^|\s)([#@/+])([^#@/+\n]{0,60})$/);
    if (!m) return null;
    return { sign: m[1], query: m[2], start: end - m[2].length - 1, end };
  }, [value, caret]);

  const teamName = useMemo(() => Object.fromEntries((teams || []).map((tm) => [tm.id, tm.name])), [teams]);
  const items = useMemo(() => {
    if (!token) return [];
    const q = fold(token.query).trim();
    const rank = (name) => {
      const n = fold(name);
      if (!q) return 1;
      if (n.startsWith(q)) return 0;
      return n.includes(q) ? 1 : -1;
    };
    const pick = (list, nameOf, toItem) => list
      .map((x) => ({ x, r: rank(nameOf(x)) }))
      .filter(({ r }) => r >= 0)
      .sort((a, b) => a.r - b.r || nameOf(a.x).localeCompare(nameOf(b.x)))
      .slice(0, SHOWN)
      .map(({ x }) => toItem(x));
    // A query with spaces stays open only while some name still starts with it.
    // (With its space: "#lum " closes the list, "#Lumen " goes on while "Lumen Co" fits.)
    const typed = fold(token.query);
    if (/\s/.test(token.query) && ![...projects.map((p) => p.name), ...people.map(personName), ...sections.map((x) => x.name),
      ...labels.map((l) => l.name)].some((n) => fold(n).startsWith(typed))) return [];
    if (token.sign === '#') {
      return pick(projects.filter((p) => !p.deleted_at && p.name), (p) => p.name,
        (p) => ({ key: p.id, label: p.name, sub: p.team_id ? teamName[p.team_id] : null, color: p.color || '#3B82F6' }));
    }
    if (token.sign === '/') {
      return pick(sections.filter((x) => x.name), (x) => x.name, (x) => ({ key: x.id, label: x.name, list: true }));
    }
    if (token.sign === '+') {
      return pick(labels.filter((l) => l.name), (l) => l.name, (l) => ({ key: l.id, label: l.name, color: l.color || '#6B7280', tag: true }));
    }
    return pick(people, personName, (m) => (m.bot_id
      ? { key: `bot:${m.bot_id}`, label: personName(m), bot: m.bot }
      : { key: m.user_id, label: personName(m), avatar: m.avatar_url }));
  }, [token, projects, people, sections, labels, teamName]);

  const hint = token?.sign === '@' && people.length === 0 && !/\s/.test(token.query) ? t('tasks.select_project_with_team') : null;
  const open = !!token && closedAt !== token.start && (items.length > 0 || !!hint);
  const active = Math.min(index, Math.max(items.length - 1, 0));

  const choose = (item) => {
    const text = `${token.sign}${item.label} `;
    const next = value.slice(0, token.start) + text + value.slice(token.end).replace(/^\s+/, '');
    const at = token.start + text.length;
    setValue(next);
    setCaret(at);
    setIndex(0);
    requestAnimationFrame(() => {
      const el = inputRef?.current;
      if (el) { el.focus(); el.setSelectionRange(at, at); }
    });
  };

  const onKeyDown = (e) => {
    if (!open || e.nativeEvent?.isComposing) return false;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!items.length) return false;
      e.preventDefault();
      setIndex((i) => (Math.min(i, items.length - 1) + (e.key === 'ArrowDown' ? 1 : items.length - 1)) % items.length);
      return true;
    }
    if ((e.key === 'Enter' || e.key === 'Tab') && items.length) {
      e.preventDefault();
      choose(items[active]);
      return true;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setClosedAt(token.start);
      return true;
    }
    return false;
  };

  const track = (e) => { setCaret(e.currentTarget.selectionStart); setIndex(0); };
  const bind = { onSelect: track, onKeyUp: (e) => { if (!['ArrowDown', 'ArrowUp'].includes(e.key)) track(e); }, onClick: track };

  /** The list under the field. `tone`: 'app' or 'desktop' (the dark panel). In place (the
   * field's container is `relative`) or, `portal`, fixed on the page under the field, for a
   * field inside something that clips (a table). */
  const popup = (tone = 'app', { portal = false, className } = {}) => {
    if (!open) return null;
    const rect = portal ? inputRef?.current?.getBoundingClientRect() : null;
    const list = (
    <div role="listbox" data-testid="token-suggest"
      style={rect ? { position: 'fixed', top: rect.bottom + 4, left: rect.left, width: Math.max(rect.width, 280) } : undefined}
      className={cn('z-50 max-h-64 overflow-y-auto py-1 rounded-lg shadow-xl', !rect && 'absolute left-0 right-0 top-full mt-1',
        tone === 'desktop' ? 'bg-[#1c1c20] border border-white/10' : 'bg-popover border border-border', className)}>
      {hint && <div className={cn('px-3 py-2 text-xs', tone === 'desktop' ? 'text-[#9a9aa3]' : 'text-muted-foreground')}>{hint}</div>}
      {items.map((item, i) => (
        <button key={item.key} type="button" role="option" aria-selected={i === active} data-testid="token-suggest-option"
          // A tap or click picks without taking the focus (and the caret) from the field.
          onMouseDown={(e) => { e.preventDefault(); choose(item); }} onMouseEnter={() => setIndex(i)}
          className={cn('w-full flex items-center gap-2.5 px-3 py-1.5 text-left text-sm',
            tone === 'desktop'
              ? cn('text-[#e4e4e8]', i === active && 'bg-white/10')
              : cn('text-foreground', i === active && 'bg-accent'))}>
          {item.bot ? <BotAvatar bot={item.bot} size={20} />
            : item.list ? <span className={cn('w-5 text-center text-xs flex-shrink-0', tone === 'desktop' ? 'text-[#85858f]' : 'text-muted-foreground')}>/</span>
            : item.tag ? <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ background: item.color }} />
            : item.color ? <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: item.color }} />
            : item.avatar ? <img src={item.avatar} alt="" className="w-5 h-5 rounded-full object-cover flex-shrink-0" />
              : <span className="w-5 h-5 rounded-full bg-indigo-500 text-white text-[10px] font-medium flex items-center justify-center flex-shrink-0">
                {item.label.charAt(0).toUpperCase()}
              </span>}
          <span className="flex-1 min-w-0 truncate">{item.label}</span>
          {item.sub && <span className={cn('text-xs truncate max-w-[45%]', tone === 'desktop' ? 'text-[#85858f]' : 'text-muted-foreground')}>{item.sub}</span>}
        </button>
      ))}
    </div>
    );
    return rect ? createPortal(list, document.body) : list;
  };

  return { open, onKeyDown, bind, popup };
}
